import { forwardRef, ReactNode, TextareaHTMLAttributes, useEffect, useState } from 'react';

/**
 * Plain replacements for the three @plasmicpkgs/react-aria pieces the generated Plasmic files used
 * (react-aria alone was ~1,250 modules, 23% of every page's dev compile). Same props, same data-*
 * state attributes (the Plasmic CSS styles `[data-selected]`, `[data-hovered]`, …), no react-aria.
 */

type VariantUpdate = (changes: Record<string, boolean>) => void;

const flag = (on: boolean) => (on ? true : undefined);

/** Replaces `inputHelpers` of registerTextArea: the textarea's onChange gets the DOM event. */
export const plainInputHelpers = {
  states: {
    value: {
      onChangeArgsToValue: (e: any) => e.target.value,
    },
  },
};

type PlainTextAreaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value'> & {
  value?: string;
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
  plasmicUpdateVariant?: VariantUpdate;
  setControlContextData?: (data: any) => void;
};

export const PlainTextArea = forwardRef<HTMLTextAreaElement, PlainTextAreaProps>(function PlainTextArea(
  { plasmicUpdateVariant, setControlContextData, resize, value, style, disabled, onFocus, onBlur, onMouseEnter, onMouseLeave, ...rest },
  ref,
) {
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  void setControlContextData;
  useEffect(() => {
    plasmicUpdateVariant?.({ disabled: !!disabled, focused, focusVisible: focused, hovered });
  }, [disabled, focused, hovered, plasmicUpdateVariant]);
  return (
    <textarea
      {...rest}
      ref={ref}
      value={value}
      disabled={disabled}
      style={{ width: '100%', ...(resize ? { resize } : {}), ...style }}
      data-focused={flag(focused)}
      data-focus-visible={flag(focused)}
      data-hovered={flag(hovered)}
      data-disabled={flag(!!disabled)}
      onFocus={e => { setFocused(true); onFocus?.(e); }}
      onBlur={e => { setFocused(false); onBlur?.(e); }}
      onMouseEnter={e => { setHovered(true); onMouseEnter?.(e); }}
      onMouseLeave={e => { setHovered(false); onMouseLeave?.(e); }}
    />
  );
});

type PlainCheckboxProps = {
  className?: string;
  children?: ReactNode;
  isSelected?: boolean;
  defaultSelected?: boolean;
  isDisabled?: boolean;
  isIndeterminate?: boolean;
  isReadOnly?: boolean;
  autoFocus?: boolean;
  value?: string;
  'aria-label'?: string;
  onChange?: (isSelected: boolean) => void;
  plasmicUpdateVariant?: VariantUpdate;
  setControlContextData?: (data: any) => void;
};

export const PlainCheckbox = forwardRef<HTMLLabelElement, PlainCheckboxProps>(function PlainCheckbox(
  { className, children, isSelected, defaultSelected, isDisabled, isIndeterminate, isReadOnly, autoFocus, value, onChange, plasmicUpdateVariant, 'aria-label': ariaLabel, ...data },
  ref,
) {
  const [inner, setInner] = useState(!!defaultSelected);
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  const selected = isSelected ?? inner;
  useEffect(() => {
    plasmicUpdateVariant?.({ selected, disabled: !!isDisabled, readonly: !!isReadOnly, indeterminate: !!isIndeterminate, focused, focusVisible: focused, hovered, pressed });
  }, [selected, isDisabled, isReadOnly, isIndeterminate, focused, hovered, pressed, plasmicUpdateVariant]);
  const { setControlContextData: _ignored, ...labelProps } = data as any;
  return (
    <label
      {...labelProps}
      ref={ref}
      className={className}
      data-selected={flag(selected)}
      data-indeterminate={flag(!!isIndeterminate)}
      data-disabled={flag(!!isDisabled)}
      data-readonly={flag(!!isReadOnly)}
      data-hovered={flag(hovered)}
      data-pressed={flag(pressed)}
      data-focused={flag(focused)}
      data-focus-visible={flag(focused)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
    >
      <input
        type="checkbox"
        value={value}
        aria-label={ariaLabel}
        autoFocus={autoFocus}
        disabled={isDisabled}
        readOnly={isReadOnly}
        checked={selected}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={e => {
          if (isReadOnly) return;
          if (isSelected === undefined) setInner(e.target.checked);
          onChange?.(e.target.checked);
        }}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0, margin: 0, pointerEvents: 'none' }}
      />
      {children}
    </label>
  );
});

type PlainListItemProps = {
  className?: string;
  children?: ReactNode;
  id?: string;
  textValue?: string;
  plasmicUpdateVariant?: VariantUpdate;
  setControlContextData?: (data: any) => void;
  onClick?: () => void;
};

/** MenuItem / list-box item: a plain option row (the Plasmic sort menu is behind a feature flag). */
export const PlainListItem = forwardRef<HTMLDivElement, PlainListItemProps>(function PlainListItem(
  { className, children, id, textValue, plasmicUpdateVariant, setControlContextData, onClick },
  ref,
) {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  void setControlContextData;
  useEffect(() => {
    plasmicUpdateVariant?.({ hovered, pressed });
  }, [hovered, pressed, plasmicUpdateVariant]);
  return (
    <div
      ref={ref}
      role="option"
      aria-selected={false}
      id={id}
      aria-label={textValue}
      className={className}
      data-hovered={flag(hovered)}
      data-pressed={flag(pressed)}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
    >
      {children}
    </div>
  );
});

/** Plain text span with the registerText `slot` prop accepted and ignored. */
export const PlainText = forwardRef<HTMLSpanElement, { className?: string; children?: ReactNode; slot?: string }>(function PlainText(
  { className, children },
  ref,
) {
  return <span ref={ref} className={className}>{children}</span>;
});
